import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { formatCompactCurrency } from './format';

interface Props {
    /** Signed delta driving the arrow direction and color. */
    delta: number | null;
    /** Text shown after the arrow, e.g. "12.46 L (8.2%)" or "1.2% vs PY". */
    text: string;
    /** For "higher is worse" metrics (overdue, unbilled): an increase shows red. */
    invert?: boolean;
    /** Placeholder when delta is null, e.g. "— vs PY". */
    nullText?: string;
    /** Tighter typography for narrow tiles (account cards). */
    compact?: boolean;
}

/** Up/down arrow + colored delta line, same conventions as the P&L summary table. */
const DeltaIndicator: React.FC<Props> = ({ delta, text, invert = false, nullText = '— vs PY', compact }) => {
    const sizeClass = compact ? 'text-[11px]' : 'text-xs';

    if (delta === null) {
        return <span className={`${sizeClass} text-gray-400`}>{nullText}</span>;
    }

    const isGood = invert ? delta < 0 : delta > 0;
    const colorClass = delta === 0 ? 'text-gray-500' : isGood ? 'text-green-700' : 'text-red-600';

    return (
        <span className={`inline-flex items-start gap-0.5 font-medium leading-4 ${sizeClass} ${colorClass}`}>
            {delta > 0 && <ArrowUp className="h-3 w-3 shrink-0 mt-0.5" />}
            {delta < 0 && <ArrowDown className="h-3 w-3 shrink-0 mt-0.5" />}
            <span className="min-w-0">{text}</span>
        </span>
    );
};

/** "12.46 L (8.2%)" — absolute growth with optional pct in brackets. */
export function revenueDeltaText(growthAmt: number, growthPct: number | null): string {
    const amt = formatCompactCurrency(Math.abs(growthAmt));
    if (growthPct === null) return amt;
    return `${amt} (${Math.abs(growthPct).toLocaleString('en-IN', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })}%)`;
}

/** "1.2% vs PY" — percentage-point delta. */
export function cmDeltaText(deltaPp: number): string {
    return `${Math.abs(deltaPp).toLocaleString('en-IN', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })}% vs PY`;
}

/** "0.42 Cr vs last run" — snapshot-over-snapshot change. */
export function snapshotDeltaText(delta: number): string {
    return `${formatCompactCurrency(Math.abs(delta))} vs last run`;
}

export default DeltaIndicator;
